/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { useTranslation } from 'react-i18next';

import { ROLE_ORDER, getRoleLabels, ROLE_COLORS } from '../constants/role-colors';
import type { PermissionCategory } from '../constants/permissions-matrix';
import { Text, UnstyledButton } from '@/shared/ui';

/**
 * Renders the role-color legend plus the "no access" indicator shown above the
 * permission accordion.
 */
export function PermissionsLegend() {
  const { t } = useTranslation('features/agency-settings');
  const roleLabels = getRoleLabels(t);
  return (
    <div style={{ display: 'flex', gap: 'var(--space-5)', flexWrap: 'wrap' }}>
      {ROLE_ORDER.map((role) => {
        const rc = ROLE_COLORS[role];
        return (
          <div key={role} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text component="span" style={{ width: 10, height: 10, borderRadius: '50%', background: rc.dot }} />
            <Text component="span" style={{ fontSize: 'var(--text-body-sm)', fontWeight: 600, color: rc.text }}>
              {roleLabels[role]}
            </Text>
          </div>
        );
      })}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text component="span" style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--text-tertiary)' }} />
        <Text component="span" style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-primary)', fontWeight: 500 }}>{t('v2.permissions.noAccess')}</Text>
      </div>
    </div>
  );
}

/**
 * Renders the expanded rows of permissions for a single accordion category.
 *
 * @param group - The permission category whose rows are rendered.
 */
function PermissionRows({ group }: { group: PermissionCategory }) {
  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)' }}>
      {group.permissions.map((perm, pIdx) => (
        <div
          key={perm.key}
          data-testid="permission-row"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr repeat(3, 80px) 24px',
            padding: '8px var(--space-4)',
            borderBottom: pIdx < group.permissions.length - 1 ? '1px solid var(--border-ultra-subtle)' : 'none',
            alignItems: 'center',
          }}
        >
          <Text component="span" style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)', paddingLeft: 'var(--space-3)' }}>
            {perm.label}
          </Text>
          {ROLE_ORDER.map((role) => {
            const has = perm[role];
            const rc = ROLE_COLORS[role];
            return (
              <div key={role} style={{ textAlign: 'center' }}>
                <Text component="span"
                  data-testid="permission-dot"
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: has ? rc.dot : 'var(--text-tertiary)',
                  }}
                />
              </div>
            );
          })}
          <Text component="span" />
        </div>
      ))}
    </div>
  );
}

/**
 * Renders a single collapsible permission category with its summary header and
 * (when open) the detailed permission rows.
 *
 * @param group - The permission category to render.
 * @param isOpen - Whether the accordion is currently expanded.
 * @param onToggle - Callback to toggle the open state for this category.
 */
export function PermissionCategoryAccordion({
  group,
  isOpen,
  onToggle,
}: {
  group: PermissionCategory;
  isOpen: boolean;
  onToggle: (category: string) => void;
}) {
  return (
    <div
      style={{
        background: 'var(--surface-primary)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}
    >
      <UnstyledButton
        onClick={() => onToggle(group.category)}
        style={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr repeat(3, 80px) 24px',
          alignItems: 'center',
          padding: 'var(--space-3) var(--space-4)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-family)',
        }}
      >
        <Text component="span" style={{ fontSize: 'var(--text-body-sm)', fontWeight: 700, color: 'var(--text-primary)', textAlign: 'left' }}>
          {group.category}
        </Text>
        {ROLE_ORDER.map((role) => {
          const count = group.permissions.filter((p) => p[role]).length;
          const total = group.permissions.length;
          const rc = ROLE_COLORS[role];
          return (
            <Text component="span" key={role} style={{ fontSize: 'var(--text-meta)', fontWeight: 600, color: rc.text, textAlign: 'center' }}>
              {count}/{total}
            </Text>
          );
        })}
        <Text component="span"
          style={{
            fontSize: 14,
            color: 'var(--text-tertiary)',
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            textAlign: 'center',
          }}
        >
          ▾
        </Text>
      </UnstyledButton>

      {isOpen && <PermissionRows group={group} />}
    </div>
  );
}
